const RELOAD_GUARD_KEY = 'chunk_load_recovery:reloaded_at';
const RELOAD_GUARD_WINDOW_MS = 60_000;
const EVICTION_TIMEOUT_MS = 3_000;

let lastChunkLoadError: Error | null = null;
let recoveryPending = false;

/** Last chunk-load failure seen in this document. Lets the splash Retry take
 * the cache-evicting path even when the error that reached the boundary was a
 * secondary one (e.g. a SecurityError from storage-blocked iframes). */
export const getLastChunkLoadError = (): Error | null => lastChunkLoadError;

/** True while an auto-recovery reload is in flight — the splash suppresses
 * error telemetry during this window so the transient secondary error doesn't
 * pollute the chunk-failure signal. */
export const isChunkLoadRecoveryPending = (): boolean => recoveryPending;

const extractAssetUrl = (error: Error): string | null =>
  error.message.match(/https?:\/\/\S+|\/assets\/\S+/)?.[0] ?? null;

// A plain reload only revalidates the document and re-serves subresources
// from the HTTP cache — including a bad response cached under the chunk URL
// (e.g. a CDN SPA fallback served as 200 text/html). `cache: 'reload'`
// re-fetches from the network and replaces the cached entry.
const evictFromHttpCache = async (error: Error): Promise<void> => {
  const url = extractAssetUrl(error);
  if (!url) {
    return;
  }
  try {
    await fetch(url, { cache: 'reload', signal: AbortSignal.timeout(EVICTION_TIMEOUT_MS) });
  } catch {
    // Best-effort: the reload below can still recover via fresh HTML.
  }
};

const reloadWindow = (): void => {
  window.location.reload();
};

export const recoverFromChunkLoadError = async (
  error: Error,
  reload = reloadWindow,
): Promise<void> => {
  await evictFromHttpCache(error);
  reload();
};

// Returns true when an auto-reload is allowed, recording it so a chunk that
// keeps failing can't reload-loop. Without working sessionStorage (storage
// can be blocked in third-party iframes) the loop can't be bounded, so the
// error is left to the splash screen instead.
const armReloadGuard = (): boolean => {
  try {
    const reloadedAt = Number(sessionStorage.getItem(RELOAD_GUARD_KEY));
    if (reloadedAt > 0 && Date.now() - reloadedAt < RELOAD_GUARD_WINDOW_MS) {
      return false;
    }
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
    return true;
  } catch {
    return false;
  }
};

/**
 * Auto-recovers from failed lazy chunk/CSS imports — typically a deploy
 * replacing hashed assets mid-session. One reload per guard window. While the
 * eviction + reload is in flight, `preventDefault()` makes the failed
 * `import()` resolve `undefined`, so a secondary error may briefly reach the
 * router boundary — `isChunkLoadRecoveryPending` lets the splash ignore it.
 * When the guard blocks (repeat failure or unusable storage), the original
 * error propagates and the splash Retry recovers via `getLastChunkLoadError`.
 */
export const installChunkLoadRecovery = (
  recover: (error: Error) => Promise<void> = recoverFromChunkLoadError,
): (() => void) => {
  const handlePreloadError = (event: WindowEventMap['vite:preloadError']) => {
    lastChunkLoadError = event.payload;
    if (!armReloadGuard()) {
      return;
    }
    recoveryPending = true;
    event.preventDefault();
    void recover(event.payload);
  };
  window.addEventListener('vite:preloadError', handlePreloadError);
  return () => {
    window.removeEventListener('vite:preloadError', handlePreloadError);
    lastChunkLoadError = null;
    recoveryPending = false;
  };
};
